/**
 * Market Indices Widget
 * 
 * Displays Baltic Dry Index and vessel type rates
 * with daily changes and mini sparkline trends.
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus, RefreshCw, ExternalLink } from 'lucide-react';
import { GlassPanel } from '@/components/layout';
import { cn } from '@/lib/cn';

interface MarketIndex {
  name: string;
  value: number;
  change: number;
  changePercent: number;
  trend: number[]; // Last 7 days
}

// Simulated market data - In production, this would come from an API
// Sources: Baltic Exchange, Clarksons, etc.
const generateMarketData = (): MarketIndex[] => {
  // Realistic BDI ranges based on historical data
  const baseValues = {
    bdi: 1450 + Math.random() * 200,
    capesize: 18500 + Math.random() * 3000,
    panamax: 14200 + Math.random() * 2000,
    supramax: 12800 + Math.random() * 1500,
    handysize: 11200 + Math.random() * 1000,
  };

  const generateTrend = (base: number, volatility: number) => {
    const trend: number[] = [];
    let value = base * (0.95 + Math.random() * 0.1);
    for (let i = 0; i < 7; i++) {
      value = value * (1 + (Math.random() - 0.5) * volatility);
      trend.push(Math.round(value));
    }
    return trend;
  };

  const bdiTrend = generateTrend(baseValues.bdi, 0.03);
  const capeTrend = generateTrend(baseValues.capesize, 0.04);
  const panamaxTrend = generateTrend(baseValues.panamax, 0.035);
  const supramaxTrend = generateTrend(baseValues.supramax, 0.03);
  const handyTrend = generateTrend(baseValues.handysize, 0.025);

  return [
    {
      name: 'Baltic Dry Index',
      value: bdiTrend[6],
      change: bdiTrend[6] - bdiTrend[5],
      changePercent: ((bdiTrend[6] - bdiTrend[5]) / bdiTrend[5]) * 100,
      trend: bdiTrend,
    },
    {
      name: 'Capesize',
      value: capeTrend[6],
      change: capeTrend[6] - capeTrend[5],
      changePercent: ((capeTrend[6] - capeTrend[5]) / capeTrend[5]) * 100,
      trend: capeTrend,
    },
    {
      name: 'Panamax',
      value: panamaxTrend[6],
      change: panamaxTrend[6] - panamaxTrend[5],
      changePercent: ((panamaxTrend[6] - panamaxTrend[5]) / panamaxTrend[5]) * 100,
      trend: panamaxTrend,
    },
    {
      name: 'Supramax',
      value: supramaxTrend[6],
      change: supramaxTrend[6] - supramaxTrend[5],
      changePercent: ((supramaxTrend[6] - supramaxTrend[5]) / supramaxTrend[5]) * 100,
      trend: supramaxTrend,
    },
    {
      name: 'Handysize',
      value: handyTrend[6],
      change: handyTrend[6] - handyTrend[5],
      changePercent: ((handyTrend[6] - handyTrend[5]) / handyTrend[5]) * 100,
      trend: handyTrend,
    },
  ];
};

// Mini sparkline component
function Sparkline({ data, positive }: { data: number[]; positive: boolean }) {
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * 60;
    const y = 20 - ((value - min) / range) * 18;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width="60" height="24" className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={positive ? '#10b981' : '#ef4444'}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* End dot */}
      <circle
        cx={(data.length - 1) / (data.length - 1) * 60}
        cy={20 - ((data[data.length - 1] - min) / range) * 18}
        r="2"
        fill={positive ? '#10b981' : '#ef4444'}
      />
    </svg>
  );
}

export function MarketIndices() {
  const [indices, setIndices] = useState<MarketIndex[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const fetchData = () => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIndices(generateMarketData());
      setLastUpdated(new Date());
      setIsLoading(false);
    }, 500);
  };

  useEffect(() => {
    fetchData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const bdi = indices[0];
  const vesselRates = indices.slice(1);

  return (
    <GlassPanel className="p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Market Indices</h3>
          <p className="text-xs text-white/40">
            Updated {lastUpdated.toLocaleTimeString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            disabled={isLoading}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={cn('w-4 h-4 text-white/60', isLoading && 'animate-spin')} />
          </button>
          <a
            href="https://www.balticexchange.com/en/data-services.html"
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            title="View on Baltic Exchange"
          >
            <ExternalLink className="w-4 h-4 text-white/60" />
          </a>
        </div>
      </div>

      {/* BDI - Featured */}
      {bdi && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl bg-gradient-to-br from-ocean-cyan/20 to-ocean-blue/20 border border-ocean-cyan/30 mb-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-ocean-cyan font-medium uppercase tracking-wider">
                Baltic Dry Index
              </p>
              <div className="flex items-baseline gap-3 mt-1">
                <span className="text-3xl font-bold text-white">
                  {bdi.value.toLocaleString()}
                </span>
                <div className={cn(
                  'flex items-center gap-1 text-sm font-medium',
                  bdi.change >= 0 ? 'text-signal-success' : 'text-signal-error'
                )}>
                  {bdi.change >= 0 ? (
                    <TrendingUp className="w-4 h-4" />
                  ) : (
                    <TrendingDown className="w-4 h-4" />
                  )}
                  <span>{bdi.change >= 0 ? '+' : ''}{bdi.change.toFixed(0)}</span>
                  <span className="text-white/40">
                    ({bdi.changePercent >= 0 ? '+' : ''}{bdi.changePercent.toFixed(2)}%)
                  </span>
                </div>
              </div>
            </div>
            <Sparkline data={bdi.trend} positive={bdi.change >= 0} />
          </div>
        </motion.div>
      )}

      {/* Vessel Rates Grid */}
      <div className="grid grid-cols-2 gap-3">
        {vesselRates.map((index, i) => (
          <motion.div
            key={index.name}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="p-3 rounded-lg bg-white/5 border border-white/10"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-white/60 font-medium">{index.name}</p>
              <Sparkline data={index.trend} positive={index.change >= 0} />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-lg font-semibold text-white">
                ${index.value.toLocaleString()}
              </span>
              <span className={cn(
                'text-xs font-medium',
                index.change >= 0 ? 'text-signal-success' : 'text-signal-error'
              )}>
                {index.change >= 0 ? '+' : ''}{index.changePercent.toFixed(1)}%
              </span>
            </div>
            <p className="text-[10px] text-white/30 mt-1">$/day TCE</p>
          </motion.div>
        ))}
      </div>

      {/* Disclaimer */}
      <p className="text-[10px] text-white/30 mt-4 text-center">
        Data for illustration purposes. Real-time data requires Baltic Exchange subscription.
      </p>
    </GlassPanel>
  );
}

export default MarketIndices;

